import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { MultiSelect } from "primereact/multiselect";
import { normalizeSegmentsList } from "../Customers/Segments/segmentUtils";
import {
  getSegment,
  getTagGroupsGraphed,
  getSegmentResponsibilities,
  getTagResponsibilities,
  saveResponsibilities,
  removeSegmentResponsibility,
  removeTagResponsibility,
} from "../../Service/api";

const ResponsibilitiesDialog = ({ visible, onHide, membership, businessId, onToast }) => {
  const { t } = useTranslation("common");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [segmentResponsibilities, setSegmentResponsibilities] = useState([]);
  const [tagResponsibilities, setTagResponsibilities] = useState([]);
  const [segments, setSegments] = useState([]);
  const [tagGroupsGraphed, setTagGroupsGraphed] = useState(null);
  const [selectedSegmentIds, setSelectedSegmentIds] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);

  const membershipId = membership?.id;

  const loadResponsibilities = useCallback(async () => {
    if (!membershipId) {
      return;
    }

    setLoading(true);
    try {
      const [segmentResp, tagResp] = await Promise.all([
        getSegmentResponsibilities(membershipId),
        getTagResponsibilities(membershipId),
      ]);

      if (segmentResp.ok) {
        setSegmentResponsibilities(segmentResp.data || []);
      }
      if (tagResp.ok) {
        setTagResponsibilities(tagResp.data || []);
      }
    } finally {
      setLoading(false);
    }
  }, [membershipId]);

  const loadOptions = useCallback(async () => {
    if (!businessId) {
      return;
    }

    const [segmentResp, tagGroupResp] = await Promise.all([
      getSegment(businessId, { page: 0, size: 1000 }),
      getTagGroupsGraphed(businessId),
    ]);

    if (segmentResp.ok) {
      setSegments(normalizeSegmentsList(segmentResp.data));
    }
    if (tagGroupResp.ok) {
      setTagGroupsGraphed(tagGroupResp.data || null);
    }
  }, [businessId]);

  useEffect(() => {
    if (!visible || !membershipId) {
      return;
    }

    setSelectedSegmentIds([]);
    setSelectedTagIds([]);
    loadResponsibilities();
    loadOptions();
  }, [visible, membershipId, loadResponsibilities, loadOptions]);

  const assignedSegmentIds = useMemo(
    () => new Set(segmentResponsibilities.map((item) => item?.segment?.id).filter(Boolean)),
    [segmentResponsibilities]
  );

  const assignedTagIds = useMemo(
    () => new Set(tagResponsibilities.map((item) => item?.tag?.id).filter(Boolean)),
    [tagResponsibilities]
  );

  const segmentOptions = useMemo(
    () =>
      (segments || [])
        .filter((segment) => segment?.id && segment?.name && !assignedSegmentIds.has(segment.id))
        .map((segment) => ({ label: segment.name, value: segment.id })),
    [segments, assignedSegmentIds]
  );

  const tagOptions = useMemo(() => {
    if (!tagGroupsGraphed?.groups) {
      return [];
    }

    return tagGroupsGraphed.groups
      .filter((group) => group?.tags?.length)
      .map((group) => ({
        label: group.name,
        items: (group.tags || [])
          .filter((tag) => tag?.id && tag?.name && !assignedTagIds.has(tag.id))
          .map((tag) => ({ label: tag.name, value: tag.id })),
      }))
      .filter((group) => group.items.length > 0);
  }, [tagGroupsGraphed, assignedTagIds]);

  const resetAndClose = () => {
    setSelectedSegmentIds([]);
    setSelectedTagIds([]);
    onHide();
  };

  const handleAdd = async () => {
    if (!membershipId || (selectedSegmentIds.length === 0 && selectedTagIds.length === 0)) {
      return;
    }

    setSaving(true);
    try {
      const response = await saveResponsibilities(membershipId, {
        segmentIds: selectedSegmentIds,
        tagIds: selectedTagIds,
      });

      if (response.ok) {
        onToast?.({
          severity: "success",
          summary: t("colleague.success"),
          detail: t("colleague.responsibilities_add_success"),
        });
        setSelectedSegmentIds([]);
        setSelectedTagIds([]);
        await loadResponsibilities();
      } else {
        onToast?.({
          severity: "error",
          summary: t("failed"),
          detail: response.data?.title || response.data?.message || t("colleague.error"),
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveSegment = async (segmentResponsibilityId) => {
    if (!membershipId) {
      return;
    }

    setRemovingId(`segment-${segmentResponsibilityId}`);
    try {
      const response = await removeSegmentResponsibility(membershipId, segmentResponsibilityId);
      if (response.ok) {
        onToast?.({
          severity: "success",
          summary: t("colleague.success"),
          detail: t("colleague.responsibilities_remove_success"),
        });
        await loadResponsibilities();
      } else {
        onToast?.({
          severity: "error",
          summary: t("failed"),
          detail: response.data?.title || t("colleague.error"),
        });
      }
    } finally {
      setRemovingId(null);
    }
  };

  const handleRemoveTag = async (tagResponsibilityId) => {
    if (!membershipId) {
      return;
    }

    setRemovingId(`tag-${tagResponsibilityId}`);
    try {
      const response = await removeTagResponsibility(membershipId, tagResponsibilityId);
      if (response.ok) {
        onToast?.({
          severity: "success",
          summary: t("colleague.success"),
          detail: t("colleague.responsibilities_remove_success"),
        });
        await loadResponsibilities();
      } else {
        onToast?.({
          severity: "error",
          summary: t("failed"),
          detail: response.data?.title || t("colleague.error"),
        });
      }
    } finally {
      setRemovingId(null);
    }
  };

  const dialogFooter = (
    <div className="flex justify-content-end gap-2">
      <Button label={t("cancel")} icon="pi pi-times" className="p-button-text" onClick={resetAndClose} disabled={saving} />
      <Button
        label={t("colleague.responsibilities_add")}
        className="responsibilities-add-btn"
        onClick={handleAdd}
        loading={saving}
        disabled={saving || (selectedSegmentIds.length === 0 && selectedTagIds.length === 0)}
      />
    </div>
  );

  const managerName = membership?.user?.name || membership?.user?.email || "";

  return (
    <Dialog
      header={t("colleague.responsibilities_title", { name: managerName })}
      visible={visible}
      style={{ width: "420px" }}
      breakpoints={{ "960px": "75vw", "640px": "95vw" }}
      onHide={resetAndClose}
      footer={dialogFooter}
      className="tier_dialog responsibilities-tier-dialog"
    >
      <div className="p-fluid responsibilities-dialog">
        <div className="mb-4">
          <label className="block mb-2 font-medium">{t("colleague.responsibilities_segments")}</label>
          {loading ? (
            <p className="text-color-secondary m-0">{t("common.loading")}</p>
          ) : segmentResponsibilities.length === 0 ? (
            <p className="text-color-secondary m-0">{t("colleague.responsibilities_none")}</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {segmentResponsibilities.map((item) => (
                <span key={item.id} className="responsibility-chip">
                  <span>{item.segment?.name}</span>
                  <Button
                    icon="pi pi-times"
                    className="p-button-rounded p-button-text p-button-sm"
                    onClick={() => handleRemoveSegment(item.id)}
                    loading={removingId === `segment-${item.id}`}
                    disabled={!!removingId}
                    aria-label={t("colleague.responsibilities_remove")}
                  />
                </span>
              ))}
            </div>
          )}
          <MultiSelect
            className="mt-2 w-full responsibilities-multiselect responsibilities-segment-multiselect"
            panelClassName="responsibilities-segment-panel"
            value={selectedSegmentIds}
            onChange={(e) => setSelectedSegmentIds(e.value || [])}
            options={segmentOptions}
            optionLabel="label"
            optionValue="value"
            placeholder={t("colleague.responsibilities_select_segments")}
            display="chip"
            showSelectAll={false}
            disabled={loading || saving}
          />
        </div>

        <div>
          <label className="block mb-2 font-medium">{t("colleague.responsibilities_tags")}</label>
          {loading ? (
            <p className="text-color-secondary m-0">{t("common.loading")}</p>
          ) : tagResponsibilities.length === 0 ? (
            <p className="text-color-secondary m-0">{t("colleague.responsibilities_none")}</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {tagResponsibilities.map((item) => (
                <span key={item.id} className="responsibility-chip">
                  <span>{item.tag?.name}</span>
                  <Button
                    icon="pi pi-times"
                    className="p-button-rounded p-button-text p-button-sm"
                    onClick={() => handleRemoveTag(item.id)}
                    loading={removingId === `tag-${item.id}`}
                    disabled={!!removingId}
                    aria-label={t("colleague.responsibilities_remove")}
                  />
                </span>
              ))}
            </div>
          )}
          <MultiSelect
            className="mt-2 w-full responsibilities-multiselect responsibilities-tag-multiselect"
            panelClassName="tag-multiselect-panel"
            value={selectedTagIds}
            onChange={(e) => setSelectedTagIds(e.value || [])}
            options={tagOptions}
            optionLabel="label"
            optionValue="value"
            optionGroupLabel="label"
            optionGroupChildren="items"
            placeholder={t("colleague.responsibilities_select_tags")}
            display="chip"
            showSelectAll={false}
            disabled={loading || saving}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default ResponsibilitiesDialog;
